/**
 * TK Custom JavaScript
 * Handles header functionality including mobile search, mega menus, and mobile navigation
 */

// Check if HeaderTK class already exists
if (typeof window.HeaderTK === 'undefined') {
  window.HeaderTK = class HeaderTK {
  constructor() {
    
    // Prevent double initialization
    if (this._reinitializing) {
      return;
    }
    
    this._reinitializing = true;
    
    // Event listener references for cleanup
    this._onResize = null;
    this._onOrient = null;
    this._docClickBound = false;
    this._onDocKeydown = null;
    this._observers = [];
    this._predictiveAbort = null;
    this._badgeInterval = null;
    
    // Multi-level mobile menu history management
    this._onPopstate = null;
    this._mlmHistoryBound = false;
    this._mlmDepth = 0;
    this._currentLevel = 'main';
    
    // Initialize document click listener once
    this.initDocumentClickHandler();
    
    // Initialize components
      this.initDesktopMegaMenu();
      this.initPromo2MegaMenu();
      this.initCustomMegaMenu();
      this.initCartCounter();
      this.initMobileSearchDrawer();
      this.initPredictiveSearch();
      this.initMegaMenuPositioning();
      this.initMultiLevelMobileMenu();
      
      // Store instance globally
      window.headerTKInstance = this;
      
      // Clear re-initializing flag
      this._reinitializing = false;
      
    }
  
  
  /**
   * Toggle dropdown with proper ARIA attributes
   */
  toggleDropdown(item, dropdown, open) {
    dropdown.setAttribute('data-visible', open ? 'true' : 'false');
    item.classList.toggle('mega-menu-open', open);
    dropdown.toggleAttribute('inert', !open);
    dropdown.setAttribute('aria-hidden', String(!open));
    
    const trigger = item.querySelector('.tk-nav__link-wrapper a, .tk-nav__link-wrapper button') || item.querySelector('.tk-nav__link-wrapper');
    if (trigger) {
      trigger.setAttribute('aria-expanded', String(open));
      if (dropdown.id) {
        trigger.setAttribute('aria-controls', dropdown.id);
      }
    }
    
    if (open) {
      const focusable = dropdown.querySelector('a, button, [tabindex]:not([tabindex="-1"])');
      if (focusable) {
        focusable.focus();
      } else {
        // No focusable element, make dropdown focusable temporarily
        dropdown.setAttribute('tabindex', '-1');
        dropdown.focus();
        dropdown.removeAttribute('tabindex');
      }
    }
  }

  /**
   * Initialize document click handler for mega menu closing
   */
  initDocumentClickHandler() {
    if (this._docClickBound) return;
    
    // Document click handler
    document.addEventListener('click', (e) => {
      document.querySelectorAll('.tk-nav__item .tk-mega-menu-dropdown[data-visible="true"]').forEach(dd => {
        const item = dd.closest('.tk-nav__item');
        if (item && !item.contains(e.target)) {
          dd.setAttribute('data-visible', 'false');
          dd.setAttribute('aria-hidden', 'true');
          dd.setAttribute('inert', '');
          item.classList.remove('mega-menu-open');
          const trigger = item.querySelector('.tk-nav__link-wrapper a, .tk-nav__link-wrapper button') || item.querySelector('.tk-nav__link-wrapper');
          trigger && trigger.setAttribute('aria-expanded', 'false');
        }
      });
    });
    
    // Document keydown handler for ESC
    this._onDocKeydown = (e) => {
      if (e.key !== 'Escape') return;
      document.querySelectorAll('.tk-nav__item .tk-mega-menu-dropdown[data-visible="true"]').forEach(dd => {
        const item = dd.closest('.tk-nav__item');
        dd.setAttribute('data-visible', 'false');
        dd.setAttribute('aria-hidden', 'true');
        dd.setAttribute('inert', '');
        item?.classList.remove('mega-menu-open');
        const trigger = item?.querySelector('.tk-nav__link-wrapper a, .tk-nav__link-wrapper button') || item?.querySelector('.tk-nav__link-wrapper');
        trigger && trigger.setAttribute('aria-expanded', 'false');
      });
    };
    document.addEventListener('keydown', this._onDocKeydown);
    
    this._docClickBound = true;
  }

  /**
   * Desktop Mega Menu
   */
    initDesktopMegaMenu() {
      const navItems = document.querySelectorAll('.tk-nav__item');
      const isHoverEnabled = this.isHoverEnabled();
      
      // Initialize all dropdowns as closed with proper accessibility
      document.querySelectorAll('.tk-mega-menu-dropdown').forEach(dd => {
        const isOpen = dd.getAttribute('data-visible') === 'true';
        dd.setAttribute('aria-hidden', String(!isOpen));
        dd.toggleAttribute('inert', !isOpen);
      });
      navItems.forEach((item, index) => {
        const dropdown = item.querySelector('.tk-mega-menu-dropdown');
        
        if (dropdown) {
          if (isHoverEnabled) {
            this.setupHoverMenu(item, null, dropdown, navItems);
          } else {
            this.setupClickMenu(item, null, dropdown);
          }
        }
      });
      
      // Handle window resize
      const resizeHandler = () => {
        const newHoverEnabled = this.isHoverEnabled();
        if (newHoverEnabled !== isHoverEnabled) {
          // Re-initialize if hover setting changed
          this.cleanupMegaMenuEventListeners();
          this.initDesktopMegaMenu();
        }
        
        // Mega menu positioning is now handled by CSS only
      };
      
      // Remove existing listeners to prevent accumulation
      if (this._onResize) window.removeEventListener('resize', this._onResize);
      if (this._onOrient) window.removeEventListener('orientationchange', this._onOrient);
      
      // Add resize listener with debouncing
      let resizeTimeout;
      this._onResize = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(resizeHandler, 100);
      };
      this._onOrient = () => setTimeout(resizeHandler, 300);
      
      window.addEventListener('resize', this._onResize);
      window.addEventListener('orientationchange', this._onOrient);
    }
  
    /**
     * Promo2 Mega Menu
     */
    initPromo2MegaMenu() {
      const promo2Menus = document.querySelectorAll('.tk-mega-menu-promo-2');
      
      promo2Menus.forEach(menu => {
        const mainItems = menu.querySelectorAll('.promo2-mainitem');
        const subItems = menu.querySelectorAll('.promo2-subitem');
        const detailImg = menu.querySelector('.promo2-detail-img');
        const detailTitle = menu.querySelector('.promo2-detail-title');
        const allSubmenus = menu.querySelector('.promo2-all-submenus');
        
        // Preload all images for instant display
        const allCollectionItems = menu.querySelectorAll('.promo2-submenu-item, .promo2-subitem');
        const imageCache = new Set();
        
        allCollectionItems.forEach((item, index) => {
          const title = item.getAttribute('data-collection-title');
          const handle = item.getAttribute('data-collection-handle');
          const image = item.getAttribute('data-collection-image');
          const hasImage = image && image.trim() !== '';
          
          // Preload image
          if (hasImage && !imageCache.has(image)) {
            imageCache.add(image);
            const preloadImg = new Image();
            preloadImg.src = image;
          }
        });
        
        if (mainItems.length > 0) {
          mainItems.forEach((mainItem, index) => {
            const link = mainItem.querySelector('.promo2-mainitem-link');
            
            // Hover event for main items (immediate response)
            mainItem.addEventListener('mouseenter', () => {
              // Remove active class from all main items
              mainItems.forEach(item => item.classList.remove('active'));
              
              // Add active class to hovered item
              mainItem.classList.add('active');
              
              // Update submenu
              this.updatePromo2Submenu(menu, index, subItems, detailImg, detailTitle);
            });
            
            // Click event - allow link navigation
            if (link) {
              link.addEventListener('click', (e) => {
                // On desktop, allow link navigation
                // On mobile, prevent and show submenu
                if (window.innerWidth <= 1024) {
                  e.preventDefault();
                  mainItems.forEach(item => item.classList.remove('active'));
                  mainItem.classList.add('active');
                  this.updatePromo2Submenu(menu, index, subItems, detailImg, detailTitle);
                }
                // On desktop, link will navigate normally
              });
            }
          });
          
          // Initialize with first item active (trigger hover, not click)
          if (mainItems[0]) {
            mainItems[0].dispatchEvent(new Event('mouseenter'));
          }
        }
        
        // Handle submenu item clicks and hover
        subItems.forEach(subItem => {
          subItem.addEventListener('click', () => {
            this.updatePromo2Detail(subItem, detailImg, detailTitle);
          });
          
          // Immediate hover response - no delay
          subItem.addEventListener('mouseenter', () => {
            // Cancel any pending updates
            if (this.hoverTimeout) {
              clearTimeout(this.hoverTimeout);
            }
            
            // Immediate update for instant response
            this.updatePromo2Detail(subItem, detailImg, detailTitle);
          });
        });
      });
    }
  
    updatePromo2Submenu(menu, mainIndex, subItems, detailImg, detailTitle) {
      const allSubmenus = menu.querySelector('.promo2-all-submenus');
      const sublist = menu.querySelector('.promo2-sublist');
      
      if (allSubmenus && sublist) {
        const targetSubmenu = allSubmenus.querySelector(`[data-main-index="${mainIndex}"]`);
        
        if (targetSubmenu) {
          // Clear current sublist
          sublist.innerHTML = '';
          
          // Add new items
          const submenuItems = targetSubmenu.querySelectorAll('.promo2-submenu-item');
          submenuItems.forEach(item => {
            const li = document.createElement('li');
            li.className = 'tk-mega-menu-subitem promo2-subitem';
            li.setAttribute('role', 'none');
            li.setAttribute('data-collection-handle', item.getAttribute('data-collection-handle'));
            li.setAttribute('data-collection-title', item.getAttribute('data-collection-title'));
            li.setAttribute('data-collection-image', item.getAttribute('data-collection-image'));
            li.setAttribute('data-collection-description', item.getAttribute('data-collection-description'));
            
            const link = document.createElement('a');
            link.href = item.getAttribute('data-collection-url');
            link.className = 'tk-link-sub promo2-link-sub';
            link.setAttribute('role', 'menuitem');
            link.textContent = item.textContent;
            
            li.appendChild(link);
            sublist.appendChild(li);
            
            // Add click and hover events
            li.addEventListener('click', () => {
              this.updatePromo2Detail(li, detailImg, detailTitle);
            });
            
            // Add hover event for image change
            li.addEventListener('mouseenter', () => {
              this.updatePromo2Detail(li, detailImg, detailTitle);
            });
          });
          
          // Update detail with first item
          const firstSubItem = sublist.querySelector('.promo2-subitem');
          if (firstSubItem) {
            this.updatePromo2Detail(firstSubItem, detailImg, detailTitle);
          }
        }
      }
    }
  
    updatePromo2Detail(subItem, detailImg, detailTitle) {
      if (!detailImg || !detailTitle) return;
      
      const image = subItem.getAttribute('data-collection-image');
      const title = subItem.getAttribute('data-collection-title');
      const description = subItem.getAttribute('data-collection-description');
      const handle = subItem.getAttribute('data-collection-handle');
      
      
      // Update title immediately
      if (title) {
        detailTitle.textContent = title;
        detailTitle.style.display = 'block';
      } else {
        detailTitle.style.display = 'none';
      }
      
      // Check if image URL is valid and not empty
      const isValidImage = image && 
                          image.trim() !== '' && 
                          !image.includes('Liquid error') && 
                          !image.includes('invalid url input') &&
                          image !== 'null' &&
                          image !== 'undefined';
      
      if (isValidImage) {
        // Check if it's the same image to avoid unnecessary updates
        const currentSrc = detailImg.getAttribute('src') || '';
        if (currentSrc === image) {
          return; // Already showing this image
        }
        
        // Show loading state immediately
        const imgWrapper = detailImg.parentElement;
        if (imgWrapper) {
          imgWrapper.classList.add('loading');
        }
        
        // Try to update immediately (image might be cached)
        const testImg = new Image();
        const startTime = Date.now();
        
        testImg.onload = () => {
          // Update image immediately
          detailImg.src = image;
          detailImg.alt = title || '';
          detailImg.style.display = 'block';
          detailImg.style.opacity = '1';
          detailImg.style.visibility = 'visible';
          detailImg.classList.remove('hide', 'hidden');
          
          // Remove loading state
          if (imgWrapper) {
            imgWrapper.classList.remove('loading');
          }
        };
        
        testImg.onerror = () => {
          // Image failed to load, try fallback
          // Remove loading state
          if (imgWrapper) {
            imgWrapper.classList.remove('loading');
          }
          
          this.showFallbackImage(detailImg, title);
        };
        
        testImg.src = image;
      } else {
        // No valid image URL, show fallback
        this.showFallbackImage(detailImg, title);
      }
    }
    
    showFallbackImage(detailImg, title) {
      // Try alternative strategies based on title
      if (title) {
        // Try couples collection for Valentine's Day
        if (title.toLowerCase().includes('valentine') || title.toLowerCase().includes('couple')) {
          const couplesImg = new Image();
          couplesImg.onload = () => {
            detailImg.src = couplesImg.src;
            detailImg.alt = title;
            detailImg.style.display = 'block';
            detailImg.style.opacity = '1';
            detailImg.style.visibility = 'visible';
          };
          couplesImg.onerror = () => {
            this.showPlaceholderImage(detailImg, title);
          };
          // Try to get collection image via Shopify
          const handle = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
          couplesImg.src = `/collections/${handle}`;
        } else {
          // Show placeholder for other collections
          this.showPlaceholderImage(detailImg, title);
        }
      } else {
        this.showPlaceholderImage(detailImg, title);
      }
    }
    
    showPlaceholderImage(detailImg, title) {
      // Keep the last valid image or hide if none exists
      const currentSrc = detailImg.getAttribute('src');
      
      if (!currentSrc || currentSrc === '' || currentSrc.includes('data:image')) {
        detailImg.style.display = 'none';
      }
      // Otherwise keep showing the last valid image
    }
    
  
    /**
     * Check if hover menu is enabled
     */
    isHoverEnabled() {
      const header = document.querySelector('.site-header');
      const hoverMenuValue = header ? header.getAttribute('data-hover-menu') : null;
      
      // Default to true if not explicitly set to false
      if (hoverMenuValue === null) {
        return true; // Default to hover enabled
      }
      
      return hoverMenuValue === 'true';
    }
  
    /**
     * Setup hover menu functionality - DÜZELTİLMİŞ
     */
    setupHoverMenu(item, details, dropdown, navItems) {
      let closeTimeout;
      let isHovering = false;

      const openDropdown = () => {
        clearTimeout(closeTimeout);
        isHovering = true;
        
        // Diğer açık menüleri kapat
        navItems.forEach(otherItem => {
          if (otherItem !== item) {
            const otherDropdown = otherItem.querySelector('.tk-mega-menu-dropdown');
            if (otherDropdown) {
              this.toggleDropdown(otherItem, otherDropdown, false);
            }
          }
        });
        
        this.toggleDropdown(item, dropdown, true);
      };

      const scheduleClose = () => {
        clearTimeout(closeTimeout);
        closeTimeout = setTimeout(() => {
          if (!isHovering) {
            this.toggleDropdown(item, dropdown, false);
          }
        }, 200);
      };

      const closeDropdown = () => {
        isHovering = false;
        clearTimeout(closeTimeout);
        this.toggleDropdown(item, dropdown, false);
      };

      // Mouse events for better hover support
      item.addEventListener('mouseenter', openDropdown);
      item.addEventListener('mouseleave', (e) => {
        const toEl = e.relatedTarget;
        if (toEl && dropdown && dropdown.contains(toEl)) {
          return; // Moving to dropdown, keep open
        }
        isHovering = false;
        scheduleClose();
      });

      if (dropdown) {
        dropdown.addEventListener('mouseenter', () => {
          clearTimeout(closeTimeout);
          isHovering = true;
          this.toggleDropdown(item, dropdown, true);
        });
        
        dropdown.addEventListener('mouseleave', (e) => {
          const toEl = e.relatedTarget;
          if (toEl && item.contains(toEl)) {
            return; // Moving back to item, keep open
          }
          isHovering = false;
          scheduleClose();
        });
      }

      // ESC key handler
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && dropdown && dropdown.getAttribute('data-visible') === 'true') {
          closeDropdown();
        }
      });
    }

  initPredictiveSearch() {
    const forms = document.querySelectorAll('[data-tk-predictive-search]');
    if (!forms.length) return;

    const debounce = (fn, wait) => {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    };

    const buildItemNode = (p) => {
      const a = document.createElement('a');
      a.className = 'tk-predictive-item';
      a.href = p.url;

      const img = document.createElement('img');
      img.className = 'tk-predictive-thumb';
      img.src = (p.image && p.image.replace(/_(?:\d+x)?\./, '_100x.')) || '';
      img.alt = p.title || '';

      const wrap = document.createElement('div');
      const t = document.createElement('div');
      t.className = 'tk-predictive-title';
      t.textContent = p.title || '';

      const pr = document.createElement('div');
      pr.className = 'tk-predictive-price';
      const price = (p.price_min || p.price || 0) / 100;
      pr.textContent = `$${price.toFixed(2)}`;

      wrap.appendChild(t); 
      wrap.appendChild(pr);
      a.appendChild(img); 
      a.appendChild(wrap);
      return a;
    };

    const fetchSuggest = async (term, signal) => {
      const url = `/search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=6`;
      const res = await fetch(url, { headers: { 'Accept': 'application/json' }, signal });
      if (!res.ok) return null;
      return res.json();
    };

    const attachFloating = (form, panel) => {
      const isDesktop = window.matchMedia && window.matchMedia('(min-width: 1200px)').matches;
      const input = form.querySelector('.tk-search-input');
      if (!input || !panel) return;

      if (!isDesktop) {
        // MOBILE: keep panel inline inside form and remove floating styles
        if (panel.classList.contains('tk-predictive-results--floating')) {
          panel.classList.remove('tk-predictive-results--floating');
        }
        if (panel.parentNode !== form) {
          form.appendChild(panel);
        }
        panel.style.width = '';
        panel.style.left = '';
        panel.style.top = '';
        return;
      }

      // DESKTOP: attach as floating panel near input
      const rect = input.getBoundingClientRect();
      if (!panel.classList.contains('tk-predictive-results--floating')) {
        panel.classList.add('tk-predictive-results--floating');
        document.body.appendChild(panel);
      }
      panel.style.width = `${Math.max(input.offsetWidth, 260)}px`;
      panel.style.left = `${Math.round(rect.left + window.scrollX)}px`;
      panel.style.top = `${Math.round(rect.bottom + window.scrollY + 6)}px`;
    };

    const onInput = debounce(async (ev) => {
      const input = ev.target;
      const form = input.closest('[data-tk-predictive-search]');
      if (!form) return;
      let panel = form.querySelector('[data-tk-predictive-results]');
      if (!panel) return;
      attachFloating(form, panel);
      const term = String(input.value || '').trim();
      if (term.length < 2) {
        panel.dataset.open = 'false';
        panel.innerHTML = '';
        return;
      }
      // Cancel previous request
      if (this._predictiveAbort) this._predictiveAbort.abort();
      this._predictiveAbort = new AbortController();
      
      try {
        const data = await fetchSuggest(term, this._predictiveAbort.signal);
        const products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
        if (!products.length) {
          panel.innerHTML = '<div class="tk-predictive-item">No results</div>';
          panel.dataset.open = 'true';
          return;
        }
        panel.innerHTML = '';
        products.forEach(p => panel.appendChild(buildItemNode(p)));
        panel.dataset.open = 'true';
      } catch (e) {
        if (e.name !== 'AbortError') {
          panel.innerHTML = '<div class="tk-predictive-item">Error loading</div>';
          panel.dataset.open = 'true';
        }
      }
    }, 180);

    forms.forEach((form) => {
      const input = form.querySelector('.tk-search-input');
      const panel = form.querySelector('[data-tk-predictive-results]');
      const popularTpl = form.querySelector('template[data-tk-popular-template]');
      if (!input || !panel) return;
      input.addEventListener('input', onInput);
      input.addEventListener('focus', (e) => {
        attachFloating(form, panel);
        if ((panel.innerHTML || '').trim().length) {
          panel.dataset.open = 'true';
        } else if (popularTpl) {
          panel.innerHTML = popularTpl.innerHTML;
          panel.dataset.open = 'true';
          this.bindPopularClicks(form, input, panel);
        }
      });
      
      // ESC to close predictive panel
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          panel.dataset.open = 'false';
          panel.innerHTML = '';
          input.blur();
        }
      });
      // Bind mobile suggestion tags inside this form (if present)
      const mobileTags = form.querySelectorAll('.tk-mobile-search-tag');
      mobileTags.forEach(btn => {
        btn.addEventListener('click', () => {
          const term = btn.textContent.trim();
          input.value = term;
          input.dispatchEvent(new Event('input', { bubbles: true }));
        });
      });
      document.addEventListener('click', (ev) => {
        if (!form.contains(ev.target) && !panel.contains(ev.target)) {
          panel.dataset.open = 'false';
        }
      });
      // Reposition on resize/scroll
      const reposition = () => attachFloating(form, panel);
      window.addEventListener('resize', reposition);
      window.addEventListener('scroll', reposition, { passive: true });
      form.addEventListener('submit', () => {
        panel.dataset.open = 'false';
      });
    });
  }

  bindPopularClicks(form, input, panel) {
    const buttons = panel.querySelectorAll('.tk-popular-item');
    buttons.forEach(btn => {
      btn.addEventListener('click', () => {
        const term = btn.getAttribute('data-term') || btn.textContent.trim();
        input.value = term;
        // Trigger input for suggestions
        input.dispatchEvent(new Event('input', { bubbles: true }));
        // Fallback: submit immediately if suggestions gelmezse
        setTimeout(() => {
          if (!panel.querySelector('.tk-predictive-item')) {
            form.submit();
          }
        }, 200);
      });
    });
  }

  
    /**
     * Setup click menu functionality
     */
    setupClickMenu(item, details, dropdown) {
      const linkWrapper = item.querySelector('.tk-nav__link-wrapper');
      
      if (linkWrapper) {
        linkWrapper.addEventListener('click', (e) => {
          e.preventDefault();
          
          const isOpen = dropdown.getAttribute('data-visible') === 'true';
          
          // Close all other dropdowns
          document.querySelectorAll('.tk-mega-menu-dropdown').forEach(otherDropdown => {
            if (otherDropdown !== dropdown) {
              otherDropdown.setAttribute('data-visible', 'false');
              // Remove class from other nav items
              const otherItem = otherDropdown.closest('.tk-nav__item');
              if (otherItem) {
                otherItem.classList.remove('mega-menu-open');
              }
            }
          });
          
          // Toggle current dropdown
          if (isOpen) {
            this.toggleDropdown(item, dropdown, false);
          } else {
            this.toggleDropdown(item, dropdown, true);
          }
        });
      }
      
      // Close when clicking outside - handled by global document click handler
    }
  
    /**
     * Mega menu positioning is now handled by CSS ID system
     * This method is disabled for ID-based positioning
     */
    adjustMegaMenuPosition(dropdown, navItem) {
      // Positioning is now handled by CSS ID system (data-menu-id)
      // JavaScript positioning disabled to prevent conflicts
      return;
    }
  
  
    /**
     * Cart Counter
     */
    initCartCounter() {
      
      // Update cart count from our elements
      const updateCartCount = () => {
        fetch('/cart.js')
          .then(response => response.json())
          .then(cart => {
            // Cart count hesapla
            let realCount = 0;
            if (cart.items) {
              cart.items.forEach(item => {
                realCount += item.quantity;
              });
            }
            this.updateCartBubble(realCount);
          })
          .catch(error => {
            // Error updating cart count
          });
      };
      
      // Update cart bubble display (both desktop and mobile icons)
      const updateCartBubble = (count) => {
        const bubbles = document.querySelectorAll('.cart-link__bubble');
        const bubbleNums = document.querySelectorAll('.cart-link__bubble-num');
        if (!bubbles.length || !bubbleNums.length) return;

        bubbles.forEach((bubble) => {
          if (count > 0) {
            bubble.style.display = 'block';
            bubble.style.visibility = 'visible';
            bubble.classList.add('cart-link__bubble--visible');
            // Animate
            bubble.classList.add('animating');
            setTimeout(() => {
              bubble.classList.remove('animating');
            }, 300);
            bubble.setAttribute('aria-hidden', 'false');
          } else {
            bubble.style.display = 'none';
            bubble.style.visibility = 'hidden';
            bubble.classList.remove('cart-link__bubble--visible');
            bubble.setAttribute('aria-hidden', 'true');
          }
        });

        bubbleNums.forEach((bubbleNum) => {
          bubbleNum.textContent = count > 0 ? String(count) : '';
        });
      };
      
      // Store updateCartBubble function for external use
      this.updateCartBubble = updateCartBubble;
      
      // Initial update
      updateCartCount();
      
      // Listen for cart updates
      document.addEventListener('cart:updated', () => {
        updateCartCount();
      });
      // Also update on other common cart events
      document.addEventListener('cart:refresh', () => {
        updateCartCount();
      });
      document.addEventListener('ajaxProduct:added', () => {
        updateCartCount();
      });

      // Fast path: optimistic badge update when POSTing to /cart/add
      this.installCartAddInterceptor?.() || (this.installCartAddInterceptor = () => {
        if (this._fetchPatched) return;
        const originalFetch = window.fetch ? window.fetch.bind(window) : null;
        if (!originalFetch) return;
        this._fetchPatched = true;
        window.fetch = (input, init) => {
          try {
            const url = typeof input === 'string' ? input : (input && input.url) || '';
            const method = (init && (init.method || 'GET')).toUpperCase();
            if (url.includes('/cart/add') && method === 'POST') {
              let addedQty = 0;
              const body = init && init.body;
              // Parse common body shapes
              if (body) {
                if (typeof body === 'string') {
                  try {
                    const data = JSON.parse(body);
                    if (Array.isArray(data?.items)) {
                      addedQty = data.items.reduce((n, it) => n + (parseInt(it.quantity) || 0), 0);
                    } else if (data && data.quantity) {
                      addedQty = parseInt(data.quantity) || 0;
                    }
                  } catch (e) { /* ignore */ }
                } else if (typeof FormData !== 'undefined' && body instanceof FormData) {
                  // Shopify ajax form posts
                  const itemsJson = body.get('items');
                  if (itemsJson) {
                    try {
                      const arr = JSON.parse(itemsJson);
                      if (Array.isArray(arr)) {
                        addedQty = arr.reduce((n, it) => n + (parseInt(it.quantity) || 0), 0);
                      }
                    } catch (e) { /* ignore */ }
                  }
                  const q = body.get('quantity');
                  if (!addedQty && q) {
                    addedQty = parseInt(q) || 0;
                  }
                }
              }
              if (addedQty > 0) {
                const bubbleNum = document.querySelector('.cart-link__bubble-num');
                const current = bubbleNum ? (parseInt(bubbleNum.textContent) || 0) : 0;
                this.updateCartBubble(current + addedQty);
                // Correct with real count shortly after
                setTimeout(updateCartCount, 400);
              }
            }
          } catch (e) { /* no-op */ }
          const p = originalFetch(input, init);
          return p;
        };
      });
      // Enable interceptor
      this.installCartAddInterceptor();
      
      // Watch for app badge changes
      this.initAppBadgeWatcher();
    }
    
    /**
     * App Badge Watcher - qsc2-badge değerini izler
     */
    initAppBadgeWatcher() {
      
      const syncFromAppBadge = () => {
        const appBadge = document.querySelector('.qsc2-badge, .qsc2-drawer-header__badge');
        if (appBadge) {
          const count = parseInt(appBadge.textContent) || 0;
          
          // Multi-app safety: never let a third-party badge
          // override Shopify's real cart count with a LOWER value.
          // Only allow the app badge to "optimistically" increase
          // the visible count (e.g. while its own drawer is open),
          // and let /cart.js be the single source of truth.
          const bubbleNum = document.querySelector('.cart-link__bubble-num');
          const current = bubbleNum ? (parseInt(bubbleNum.textContent) || 0) : 0;
          
          if (count > current) {
            this.updateCartBubble(count);
          }
          return true;
        }
        return false;
      };
      
      // Try to sync immediately
      if (syncFromAppBadge()) {
      } else {
        
      }
      
      // Watch for app badge element to appear
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            // Check if app badge was added
            const addedNodes = Array.from(mutation.addedNodes);
            addedNodes.forEach(node => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                const appBadge = node.querySelector ? node.querySelector('.qsc2-badge, .qsc2-drawer-header__badge') : null;
                if (appBadge || node.classList?.contains('qsc2-badge')) {
                 
                  syncFromAppBadge();
                }
              }
            });
          }
        });
      });
      
      // Start observing
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
      
      // Store observer for cleanup
      this._observers.push(observer);
      
      // Also watch for changes to existing app badge
      const watchExistingBadge = () => {
        const appBadge = document.querySelector('.qsc2-badge, .qsc2-drawer-header__badge');
        if (appBadge) {
         
          const badgeObserver = new MutationObserver(() => {
           
            syncFromAppBadge();
          });
          
          badgeObserver.observe(appBadge, {
            childList: true,
            characterData: true,
            subtree: true
          });
          
          // Store observer for cleanup
          this._observers.push(badgeObserver);
        } else {
          // Retry after delay
          setTimeout(watchExistingBadge, 1000);
        }
      };
      
      watchExistingBadge();
      
      // PERF: P0 fix - Removed 2s polling interval; MutationObserver + cart:updated events are sufficient
      // This saves ~2ms CPU every 2 seconds and eliminates unnecessary badge checks
    }
  
    
  
    /**
     * Cleanup mega menu event listeners
     */
    cleanupMegaMenuEventListeners() {
      // Remove existing event listeners
      const navItems = document.querySelectorAll('.tk-nav__item');
      navItems.forEach(item => {
        const details = item.querySelector('.tk-nav__details');
        const summary = details?.querySelector('summary');
        
        if (summary) {
          const newSummary = summary.cloneNode(true);
          summary.parentNode.replaceChild(newSummary, summary);
        }
      });
    }
  
  
  /**
   * Mega Menu Positioning - ID sistemi için devre dışı
   */
initMegaMenuPositioning() {
  // ID bazlı CSS positioning sistemi kullanıldığı için bu fonksiyon devre dışı
  
  return;
}


  // Dinamik Promo2 Preview Görsel Değiştirme Sistemi
  /**
   * Mobile Search Drawer
   */
  initMobileSearchDrawer() {
    const openButtons = document.querySelectorAll('.tk-mobile-search-toggle');
    const overlay = document.querySelector('.tk-mobile-search-overlay');
    const closeButtons = document.querySelectorAll('[data-mobile-search-close]');

    if (!overlay || openButtons.length === 0) {
      return;
    }

    const open = () => {
      overlay.classList.add('active');
      document.body.style.overflow = 'hidden';
      // Focus first input inside predictive search if exists
      const input = overlay.querySelector('input[type="search"], input[name="q"], input');
      if (input) {
        setTimeout(() => input.focus(), 50);
      }
    };

    const close = () => {
      overlay.classList.remove('active');
      document.body.style.overflow = '';
    };

    openButtons.forEach(btn => btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      open();
    }));

    closeButtons.forEach(btn => btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      close();
    }));

    // Click outside to close
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        close();
      }
    });

    // ESC to close - handled by global document keydown handler
  }

  /**
   * Multi-Level Mobile Menu (legacy-like behavior)
   */
  initMultiLevelMobileMenu() {
    if (this._mlmBound) {
      // zaten bağlandı; tekrar kurma
      return;
    }
    this._mlmBound = true;
    
    // Helper: menü kökünü ve güvenli seçicileri kullan
    const getMenuRoot = () => document.querySelector('[data-multi-level-menu]');

    const setActiveLevel = (menuRoot, targetLevelEl) => {
      if (!menuRoot || !targetLevelEl) return;

      // Tüm seviyeleri pasif yap
      menuRoot.querySelectorAll('.multi-level-menu-level').forEach(level => {
        level.setAttribute('data-level-active', 'false');
        level.classList.remove('is-active');
        level.style.display = 'none';
        // aria-hidden'ı sadece display: none olduğunda ayarla
        level.setAttribute('aria-hidden', 'true');
        
        // Gizli elementlerdeki focus edilebilir elementlerin focus'unu kaldır
        const focusableElements = level.querySelectorAll('button, a, input, select, textarea, [tabindex]:not([tabindex="-1"])');
        focusableElements.forEach(el => {
          if (document.activeElement === el) {
            el.blur();
          }
        });
      });

      // Hedef seviyeyi aktif yap
      targetLevelEl.setAttribute('data-level-active', 'true');
      targetLevelEl.classList.add('is-active');
      targetLevelEl.style.display = ''; // CSS'in varsayılanını kullan (örn. flex)
      targetLevelEl.setAttribute('aria-hidden', 'false');
    };

    const updateFooterVisibility = (menuRoot, levelType) => {
      const footer = menuRoot?.querySelector('.multi-level-mobile-menu-footer');
      if (!footer) return;
      footer.style.display = (levelType === 'main') ? 'flex' : 'none';
    };

    // Ana seviyeye sıfırla
    const resetToMainLevel = () => {
      const menuRoot = getMenuRoot();
      if (!menuRoot) return;
      let mainEl = menuRoot.querySelector('.multi-level-menu-level[data-level="main"]');
      if (!mainEl) {
        mainEl = menuRoot.querySelector('.multi-level-menu-level'); // ilk bulduğunu aç
      }
      if (mainEl) {
        setActiveLevel(menuRoot, mainEl);
        updateFooterVisibility(menuRoot, 'main');
      }
    };

    // Belirli seviyeyi göster
    const showLevel = (levelType, menuItem, parent) => {
      const menuRoot = getMenuRoot();
      if (!menuRoot) return;

      let targetLevel = null;
      if (levelType === 'sub') {
        targetLevel = menuRoot.querySelector(`.multi-level-menu-level[data-level="sub"][data-parent="${menuItem}"]`);
      } else if (levelType === 'sub-sub') {
        targetLevel = menuRoot.querySelector(`.multi-level-menu-level[data-level="sub-sub"][data-parent="${menuItem}"][data-sub-parent="${parent}"]`);
      } else {
        targetLevel = menuRoot.querySelector(`.multi-level-menu-level[data-level="main"]`);
      }

      if (!targetLevel) {
        // Hedef bulunamazsa güvenli geri dönüş: ana seviyeye dön
        resetToMainLevel();
        return;
      }

        setActiveLevel(menuRoot, targetLevel);
        updateFooterVisibility(menuRoot, levelType);
        
        // history'ye seviyeyi ekle
        if (this._mlmHistoryBound) {
          history.pushState({ tkmlm: true, level: levelType, menuItem, parent }, '', '');
          this._mlmDepth += 1;
          this._currentLevel = levelType;
        }
    };

    // Geri butonu davranışı - History API kullanarak
    const handleBack = () => {
      // tek kaynak: tarayıcının history geri akışı
      history.back();
    };

    // History API handlers
    const onPopstate = (ev) => {
      const st = ev.state;
      // bizim state'imiz değilse (ör: sayfa tarihi) menüyü kapat ve çık
      if (!st || st.tkmlm !== true) {
        menuRoot.classList.remove('active');
        document.body.style.overflow = '';
        resetToMainLevel();
        unbindMlmHistory();
        this._mlmDepth = 0;
        return;
      }
      // bizim state ise seviyeyi uygula
      if (!menuRoot.classList.contains('active')) {
        menuRoot.classList.add('active');
        document.body.style.overflow = 'hidden';
      }
      if (st.level === 'main') {
        resetToMainLevel();
      } else if (st.level === 'sub') {
        // showLevel tekrar push etmesin diye önce geçici olarak unbind
        const prev = this._mlmHistoryBound; this._mlmHistoryBound = false;
        showLevel('sub', st.menuItem, st.parent);
        this._mlmHistoryBound = prev;
      } else if (st.level === 'sub-sub') {
        const prev = this._mlmHistoryBound; this._mlmHistoryBound = false;
        showLevel('sub-sub', st.menuItem, st.parent);
        this._mlmHistoryBound = prev;
      }
      this._currentLevel = st.level;
      // derinlik tahmini: st.level'e göre azaltılmasını tarayıcı yönetiyor; biz sadece 0'a düşerse kapatırız
      if (history.state?.tkmlm !== true) {
        // güvenli kapanış
        menuRoot.classList.remove('active');
        document.body.style.overflow = '';
        unbindMlmHistory();
        this._mlmDepth = 0;
      } else {
        // yaklaşık takip: 0'ın altına düşmesini engelle
        this._mlmDepth = Math.max(0, this._mlmDepth - 1);
      }
    };

    const bindMlmHistory = () => {
      if (this._mlmHistoryBound) return;
      this._onPopstate = onPopstate;
      window.addEventListener('popstate', this._onPopstate);
      this._mlmHistoryBound = true;
    };
    
    const unbindMlmHistory = () => {
      if (!this._mlmHistoryBound) return;
      window.removeEventListener('popstate', this._onPopstate);
      this._onPopstate = null;
      this._mlmHistoryBound = false;
    };

    // Initialize mobile menu
    const menuRoot = getMenuRoot();
    const hamburgerButtons = document.querySelectorAll('[data-mobile-menu-toggle]');
    const closeButtons = document.querySelectorAll('[data-mobile-menu-close]');
    const backButtons = menuRoot ? menuRoot.querySelectorAll('[data-menu-back]') : [];

    if (!menuRoot || !hamburgerButtons.length) {
      this._mlmBound = false; // bir sonraki sayfada tekrar denenebilsin
      return;
    }

      const openMenu = () => {
        menuRoot.classList.add('active');
        document.body.style.overflow = 'hidden';
        resetToMainLevel();
        bindMlmHistory();
        // menü açılış state'i
        history.pushState({ tkmlm: true, level: 'main' }, '', '');
        this._mlmDepth = 1;
        this._currentLevel = 'main';
      };

      const closeMenu = () => {
        menuRoot.classList.remove('active');
        document.body.style.overflow = '';
        resetToMainLevel();
        // eklediğimiz tüm state'leri geri al
        if (this._mlmDepth > 0) {
          history.go(-this._mlmDepth);
          this._mlmDepth = 0;
        }
        unbindMlmHistory();
      };

    // Toggle'lar
    hamburgerButtons.forEach(btn => {
      if (btn.getAttribute('data-initialized') === 'true') return;
      btn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); openMenu(); });
      btn.setAttribute('data-initialized', 'true');
    });

    closeButtons.forEach(btn => {
      btn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); closeMenu(); });
    });

    backButtons.forEach(btn => {
      if (btn.getAttribute('data-initialized') === 'true') return;
      btn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); handleBack(); });
      btn.setAttribute('data-initialized', 'true');
    });

    // Dropdown linkleri (sadece bu menü kökü içinde ara)
    menuRoot.querySelectorAll('.multi-level-menu-link--has-dropdown').forEach(btn => {
      if (btn.getAttribute('data-initialized') === 'true') return;
      btn.addEventListener('click', (e) => {
        e.preventDefault(); e.stopPropagation();
        const levelType = btn.getAttribute('data-next-level');
        const menuItem  = btn.getAttribute('data-menu-item');
        const parent    = btn.getAttribute('data-parent');
        showLevel(levelType, menuItem, parent);
      });
      btn.setAttribute('data-initialized', 'true');
    });

    // Root'a tıklayınca kapanma
    menuRoot.addEventListener('click', (e) => {
      if (e.target === menuRoot) closeMenu();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && menuRoot.classList.contains('active')) closeMenu();
    });

    // Initialize
    resetToMainLevel();
  }

  // Koleksiyon verilerini al (dinamik olarak DOM'dan)
  getCollectionData(collectionTitle) {
    // DOM'dan koleksiyon verilerini al
    const collectionItems = document.querySelectorAll('.promo2-subitem');
    const collectionData = {};
    
    collectionItems.forEach(item => {
      const handle = item.getAttribute('data-collection-handle');
      const title = item.getAttribute('data-collection-title');
      const image = item.getAttribute('data-collection-image');
      const description = item.getAttribute('data-collection-description');
      
      if (handle && title) {
        collectionData[title] = {
          handle: handle,
          title: title,
          image: image,
          description: description,
          images: [image] // Şimdilik tek görsel, daha sonra genişletilebilir
        };
      }
    });
    
    return collectionData[collectionTitle] || null;
  }

  // Dinamik görsel değiştirme başlat
  startDynamicImageChange(imageContainer, images, startIndex = 0) {
    let currentIndex = startIndex;
    
    // Loading state ekle
    imageContainer.classList.add('loading');
    
    const changeImage = () => {
      if (!imageContainer.classList.contains('loading')) return;
      
      currentIndex = (currentIndex + 1) % images.length;
      const newImageSrc = images[currentIndex];
      
      // Yeni görsel oluştur
      const newImage = new Image();
      newImage.className = 'promo2-collection-image dynamic-transition';
      newImage.style.width = '100%';
      newImage.style.height = '200px';
      newImage.style.objectFit = 'cover';
      newImage.style.borderRadius = '8px';
      newImage.alt = 'Dynamic Collection Image';
      
      newImage.onload = () => {
        // Mevcut görseli fade out yap
        const currentImg = imageContainer.querySelector('img');
        if (currentImg) {
          currentImg.classList.add('image-fade-out');
          
          setTimeout(() => {
            // Yeni görseli ekle
            newImage.classList.add('image-fade-in');
            imageContainer.appendChild(newImage);
            
            // Eski görseli kaldır
            if (currentImg.parentNode) {
              currentImg.parentNode.removeChild(currentImg);
            }
            
            // Loading state kaldır
            imageContainer.classList.remove('loading');
            
            // Sonraki değişim için zamanlayıcı
            setTimeout(() => {
              if (imageContainer.classList.contains('loading')) {
                changeImage();
              }
            }, 2000); // 2 saniye bekle
            
          }, 300);
        }
      };
      
      newImage.onerror = () => {
        imageContainer.classList.remove('loading');
      };
      
      newImage.src = newImageSrc;
    };
    
    // İlk değişimi başlat
    setTimeout(changeImage, 500);
  }

  // Dinamik görsel değiştirme durdur
  stopDynamicImageChange(imageContainer, originalImageSrc) {
    imageContainer.classList.remove('loading');
    
    // Orijinal görsele dön
    const currentImg = imageContainer.querySelector('img');
    if (currentImg && currentImg.src !== originalImageSrc) {
      const originalImage = new Image();
      originalImage.className = 'promo2-collection-image dynamic-transition';
      originalImage.style.width = '100%';
      originalImage.style.height = '200px';
      originalImage.style.objectFit = 'cover';
      originalImage.style.borderRadius = '8px';
      originalImage.alt = 'Original Collection Image';
      
      originalImage.onload = () => {
        currentImg.classList.add('image-fade-out');
        
        setTimeout(() => {
          originalImage.classList.add('image-fade-in');
          imageContainer.appendChild(originalImage);
          
          if (currentImg.parentNode) {
            currentImg.parentNode.removeChild(currentImg);
          }
        }, 300);
      };
      
      originalImage.src = originalImageSrc;
    }
  }

  /**
   * Custom Mega Menu Initialization
   */
  initCustomMegaMenu() {
    const customMenus = document.querySelectorAll('.tk-mega-menu-custom');
    
    customMenus.forEach(menu => {
      const menuItems = menu.querySelectorAll('.tk-mega-menu-custom-item');
      
      if (menuItems.length > 0) {
        // Add hover effects for custom menu items
        menuItems.forEach(item => {
          const image = item.querySelector('.tk-mega-menu-custom-image');
          const links = item.querySelectorAll('.tk-mega-menu-custom-link');
          
          // Image hover effect
          if (image) {
            item.addEventListener('mouseenter', () => {
              image.style.transform = 'scale(1.05)';
            });
            
            item.addEventListener('mouseleave', () => {
              image.style.transform = 'scale(1)';
            });
          }
          
          // Link hover effects
          links.forEach(link => {
            link.addEventListener('mouseenter', () => {
              link.style.transform = 'translateY(-1px)';
              link.style.boxShadow = '0 2px 8px rgba(255, 183, 0, 0.3)';
            });
            
            link.addEventListener('mouseleave', () => {
              link.style.transform = 'translateY(0)';
              link.style.boxShadow = 'none';
            });
          });
        });
      }
    });
  }

    // Cleanup function
    cleanup() {
      // Disconnect all observers
      this._observers.forEach(observer => observer.disconnect());
      this._observers = [];
      
      // Remove event listeners
      if (this._onResize) {
        window.removeEventListener('resize', this._onResize);
        this._onResize = null;
      }
      if (this._onOrient) {
        window.removeEventListener('orientationchange', this._onOrient);
        this._onOrient = null;
      }
      if (this._onDocKeydown) {
        document.removeEventListener('keydown', this._onDocKeydown);
        this._onDocKeydown = null;
      }
      
      // Clear intervals
      if (this._badgeInterval) {
        clearInterval(this._badgeInterval);
        this._badgeInterval = null;
      }
      
      // Abort pending requests
      if (this._predictiveAbort) {
        this._predictiveAbort.abort();
        this._predictiveAbort = null;
      }
      
      // Reset flags
      this._docClickBound = false;
      this._mlmBound = false;
      
      // Multi-level mobile menu history cleanup
      if (this._mlmHistoryBound && this._onPopstate) {
        window.removeEventListener('popstate', this._onPopstate);
        this._onPopstate = null;
        this._mlmHistoryBound = false;
      }
      this._mlmDepth = 0;
      this._currentLevel = 'main';
    }
  };
} // End of HeaderTK class

// Initialize immediately when DOM is ready
function initializeHeader() {
    if (!window.headerTKInstance) {
      window.headerTKInstance = new window.HeaderTK();
    } else {
      // Re-initialize mega menu
      if (window.headerTKInstance.initDesktopMegaMenu) {
        window.headerTKInstance._reinitializing = true;
        window.headerTKInstance.initDesktopMegaMenu();
        window.headerTKInstance._reinitializing = false;
      }
    }
  }

  // Optimized initialization - only run once
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeHeader);
  } else {
    // DOM is already ready
    initializeHeader();
  }